import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5F2]">
        <Card className="w-full max-w-md border-stone-200 rounded-2xl shadow-none">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-[#6B8E7B]" />
            </div>
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-['Outfit']">Check Your Email</h2>
            <p className="text-stone-600 mb-6">
              If an account exists for {email}, you'll receive a password reset link shortly.
            </p>
            <Link to="/login">
              <Button className="bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5F2]">
      <Card className="w-full max-w-md border-stone-200 rounded-2xl shadow-none">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="h-8 w-8 text-[#6B8E7B]" fill="#6B8E7B" />
            <span className="text-2xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
          </div>
          <CardTitle className="text-2xl font-['Outfit']">Forgot Password</CardTitle>
          <CardDescription className="text-stone-500">
            Enter your email and we'll send you a reset link
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nurse@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-xl"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full h-11"
              disabled={loading}
              data-testid="forgot-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-[#6B8E7B] hover:underline font-medium text-sm inline-flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      toast.success('Password reset successful!');
      navigate('/login');
    } catch (error) {
      setError(error.response?.data?.detail || 'Reset failed. Link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5F2]">
        <Card className="w-full max-w-md border-stone-200 rounded-2xl shadow-none">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-['Outfit']">Invalid Link</h2>
            <p className="text-stone-600 mb-6">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full">
                Request New Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5F2]">
      <Card className="w-full max-w-md border-stone-200 rounded-2xl shadow-none">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="h-8 w-8 text-[#6B8E7B]" fill="#6B8E7B" />
            <span className="text-2xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
          </div>
          <CardTitle className="text-2xl font-['Outfit']">Reset Password</CardTitle>
          <CardDescription className="text-stone-500">
            Enter your new password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-xl"
                data-testid="reset-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="rounded-xl"
                data-testid="reset-confirm-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full h-11"
              disabled={loading}
              data-testid="reset-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export { ForgotPasswordPage, ResetPasswordPage };
